<!--
  AddressSearchField: the address search field of createAddress / editAddress (geocoder results from Google Maps and
  OpenStreetMap). A thin wrapper around v-autocomplete: every prop, attribute and event not declared here is passed
  through ($attrs / $listeners), so values, the selected item and the emitted Place stay those of the parent.
  Presentation only:
  - the menu is attached to the field (not to the app root), so it sits directly below the input, scrolls with
    scrolling dialog bodies (CalDialogShell, createStepperDialog …) and never ends up detached far below the field
  - opens upwards when the visible area of the scroll container (or the window) has no room below; max height fits
    the available space, the list scrolls inside
  - falls back to the app root (repositioned while scrolling) when an ancestor clips overflow (tabs, stepper) or the
    scroll container is too small for the list (short, non-scrolling dialogs)
  - rows: pin, street + house number bold, "PLZ Ort" as second line; the source attribution ("Google Maps",
    OpenStreetMap) once at the bottom of the list instead of on every row
  - loading skeleton and empty state "Keine Adresse gefunden" with a link to the manual input (event `manual`)
  Props: items, loading, searchActive (the typed text differs from the selection: only then loading/empty states are
         shown), allowManualInput
  Events: everything of v-autocomplete, plus manual (the user chose "Adresse manuell eingeben" in the empty state)
-->
<template>
  <v-autocomplete ref="ac" :class="{ 'addr-ac--open-up': openUp }" :hide-no-data="!searchActive" :items="items" :loading="loading"
                  :attach="attached" :menu-props="menuProps" class="addr-ac" no-filter return-object v-bind="$attrs" v-on="$listeners"
                  @focus="onFocus">
    <template v-slot:prepend-item>
      <div v-if="loading && items.length && searchActive" class="addr-ac-hint" @mousedown.prevent>
        <v-progress-circular indeterminate size="12" width="2"/>
        Suche läuft …
      </div>
    </template>

    <template v-slot:item="{ item }">
      <div class="addr-ac-item">
        <span class="addr-ac-item__pin"><v-icon small>mdi-map-marker-outline</v-icon></span>
        <span class="addr-ac-item__body">
          <span class="addr-ac-item__title">{{ linesOf(item).main }}</span>
          <span v-if="linesOf(item).sub" class="addr-ac-item__sub">{{ linesOf(item).sub }}</span>
        </span>
      </div>
    </template>

    <template v-slot:append-item>
      <div v-if="attribution" class="addr-ac-attribution" @mousedown.prevent>{{ attribution }}</div>
    </template>

    <template v-slot:no-data>
      <div class="addr-ac-empty" @mousedown.prevent>
        <template v-if="loading">
          <div v-for="n in 3" :key="n" class="addr-ac-skeleton">
            <span class="addr-ac-skeleton__pin"></span>
            <span class="addr-ac-skeleton__lines">
              <span class="addr-ac-skeleton__line"></span>
              <span class="addr-ac-skeleton__line addr-ac-skeleton__line--short"></span>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="addr-ac-empty__title">
            <v-icon small>mdi-map-marker-off-outline</v-icon>
            Keine Adresse gefunden
          </div>
          <div class="addr-ac-empty__text">Straße, Hausnummer und Ort prüfen</div>
          <button v-if="allowManualInput" class="addr-ac-empty__link" type="button" @click.stop="chooseManual">
            <v-icon x-small>mdi-pencil-outline</v-icon>
            Adresse manuell eingeben
          </button>
        </template>
      </div>
    </template>

    <!-- Other slots of the wrapping component are passed through unchanged -->
    <template v-for="name in passedSlotNames()" v-slot:[name]>
      <slot :name="name"/>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import Vue from 'vue';
import {addressLines, addressMenuLayout, AddressSearchItemLike, sourceAttribution} from './addressSearchField.helper';

const OWN_SLOTS = ['prepend-item', 'item', 'append-item', 'no-data'];

interface MenuRef extends Vue {
  isActive: boolean;
  relativeYOffset: number;
  updateDimensions(): void;
}

/** True when an ancestor below the scroll container clips overflowing content (v-window / tabs, stepper content …) */
function hasClippingAncestor(el: HTMLElement, scrollParent: HTMLElement | null): boolean {
  let node = el.parentElement;
  while (node && node !== scrollParent && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (['hidden', 'clip'].includes(style.overflowY) || ['hidden', 'clip'].includes(style.overflowX)) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** Nearest scrolling ancestor (dialog body); null = the window */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  let node = el ? el.parentElement : null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export default Vue.extend({
  name: 'AddressSearchField',
  inheritAttrs: false,
  props: {
    items: {type: Array as () => AddressSearchItemLike[], default: () => []},
    loading: {type: Boolean, default: false},
    /** The typed text differs from the selected address (a search is running or finished) */
    searchActive: {type: Boolean, default: false},
    allowManualInput: {type: Boolean, default: true},
  },
  data: () => ({
    /** Menu inside the field (follows scrolling by itself); false = at the app root (clipping container or no room),
     *  repositioned while scrolling */
    attached: true,
    openUp: false,
    maxHeight: 320,
    menuOpen: false,
    frame: 0,
  }),
  computed: {
    attribution(): string {
      return sourceAttribution(this.items);
    },
    menuProps(): Record<string, unknown> {
      return {
        offsetY: true,
        top: this.openUp,
        nudgeBottom: this.openUp ? 0 : 4,
        nudgeTop: this.openUp ? 4 : 0,
        maxHeight: this.maxHeight,
        contentClass: 'addr-ac-menu',
        closeOnContentClick: false,
      };
    },
  },
  watch: {
    items: 'scheduleLayout',
    loading: 'scheduleLayout',
    searchActive: 'scheduleLayout',
  },
  mounted() {
    this.$nextTick(this.checkAttach);
    const ac = this.$refs.ac as Vue;
    ac.$watch('isMenuActive', (active: boolean) => {
      this.menuOpen = active;
      if (active) {
        window.addEventListener('scroll', this.scheduleLayout, true);
        window.addEventListener('resize', this.scheduleLayout);
        // Every opening starts below the field; layout() flips it up only when there is no room
        this.openUp = false;
        this.layout();
      } else {
        this.removeListeners();
        this.checkAttach();
      }
    });
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    linesOf(item: AddressSearchItemLike) {
      return addressLines(item);
    },
    passedSlotNames(): string[] {
      return Object.keys(this.$slots).filter((name) => name !== 'default' && !OWN_SLOTS.includes(name));
    },
    onFocus() {
      if (!this.menuOpen) {
        this.checkAttach();
      }
      this.scheduleLayout();
    },
    /** Attaching is only possible when nothing between field and scroll container cuts the menu off */
    checkAttach() {
      const el = (this.$refs.ac as Vue | undefined)?.$el as HTMLElement | undefined;
      if (el) {
        this.attached = !hasClippingAncestor(el, scrollParentOf(el));
      }
    },
    removeListeners() {
      window.removeEventListener('scroll', this.scheduleLayout, true);
      window.removeEventListener('resize', this.scheduleLayout);
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    },
    scheduleLayout() {
      if (this.frame) {
        return;
      }
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.layout();
      });
    },
    /**
     * Direction and max height of the menu from the free space in the visible part of the scroll container. When the
     * container (e.g. a small dialog) has no room on either side, the menu moves to the app root for this opening and
     * uses the window instead.
     */
    layout() {
      const ac = this.$refs.ac as Vue | undefined;
      const slot = ac?.$el?.querySelector('.v-input__slot') as HTMLElement | null;
      if (!ac || !slot) {
        return;
      }
      const field = slot.getBoundingClientRect();
      const parent = scrollParentOf(ac.$el as HTMLElement);
      const box = parent ? parent.getBoundingClientRect() : {top: 0, bottom: window.innerHeight};
      if (field.bottom < box.top || field.top > box.bottom) {
        // Field scrolled out of sight: close instead of leaving the list behind
        (ac as Vue & { isMenuActive: boolean }).isMenuActive = false;
        return;
      }
      const input = {
        fieldTop: field.top,
        fieldBottom: field.bottom,
        boxTop: Math.max(box.top, 0),
        boxBottom: Math.min(box.bottom, window.innerHeight),
        rows: this.items.length || (this.searchActive ? (this.loading ? 3 : 2) : 0),
        withAttribution: !!this.attribution,
        openUp: this.openUp,
      };
      let result = addressMenuLayout(input);
      if (this.attached && !result.fits) {
        // Start below again: the direction chosen inside the container says nothing about the window
        this.attached = false;
        input.openUp = false;
      }
      if (!this.attached) {
        result = addressMenuLayout({...input, boxTop: 0, boxBottom: window.innerHeight});
      }
      const changed = result.openUp !== this.openUp || result.maxHeight !== this.maxHeight;
      this.openUp = result.openUp;
      this.maxHeight = result.maxHeight;
      const menu = ac.$refs.menu as MenuRef | undefined;
      if (menu && menu.isActive && (changed || this.openUp || !this.attached)) {
        // Opening upwards depends on the content height (changes with the results); a menu at the app root
        // has to follow the field while the dialog scrolls
        this.$nextTick(() => {
          if (!this.attached) {
            // Vuetify keeps the offset of the field from attached mode for a fixed (dialog) activator: reset it
            menu.relativeYOffset = 0;
          }
          menu.updateDimensions();
        });
      }
    },
    chooseManual() {
      const ac = this.$refs.ac as Vue & { blur?: () => void; isMenuActive?: boolean } | undefined;
      if (ac) {
        ac.isMenuActive = false;
        if (typeof ac.blur === 'function') {
          ac.blur();
        }
      }
      this.$emit('manual');
    },
  },
});
</script>

<style src="./addressSearchField.css"></style>
